/**
 * drawn_water — THE DRAWN SURFACE of the lakes and rivers, for everything that must agree with the picture (2026-09-30).
 *
 * The underwater waterline (a ~7 cm near-plane lens, where a millimetre of disagreement is ~0.018 screen heights — THE
 * LENS LAW) compared the camera against a body's LEVEL, but what the renderer draws is not that level:
 *  - a LAKE is one region mesh, and its nodes' heights depend on the BAKE — the level is the window's own spill against
 *    the baked one (min(baked, max(spill, baked − free-board))) and the fill varies per node by a fraction of a
 *    millimetre; measured 2026-09-30, two consecutive region meshes of ONE lake stood at 69.6603 and 69.6523 m, and the
 *    level the waterline read (the descriptor / the bake cache) was ~5 mm off the mesh on screen (a band of
 *    surface-seen-from-above under the veil, impl-water-bodies §1.3). Its DRY nodes are also sunk `LAKE_DRY_SINK_M`
 *    under the terrain, so a triangle by a gentle shore tilts under the level;
 *  - a RIVER ribbon is drawn `RIVER_RIBBON_LIFT` above its water (4 cm — 0.7 screen heights of waterline).
 * One builder for the lake sheet (the module draws it, the harness rasterises it) and one query that solves its triangle
 * (the engine's `solveDrawnTriangle`, THE RIDER LAW's walk) keep the two from ever disagreeing again.
 */
import * as THREE from 'three/webgpu';
import { solveDrawnTriangle, type DrawnGrid } from 'enginex/environment/water/drawnSurface.js';
import { LAKE_GRID_N, LAKE_REGION_SIZE, type LakeRegion } from './lakes.js';

/** A dry lake node is sunk this far under the terrain (the old 3 m sink wrapped every lake in a visible cliff skirt). */
export const LAKE_DRY_SINK_M = 0.35;
/** A river ribbon is drawn this far above its water level (it fights z with the bed, never the bank). */
export const RIVER_RIBBON_LIFT = 0.04;

/** The lake region's drawn grid: node (col, row) at ABSOLUTE (originX + col·spacing, originZ + row·spacing). */
export function lakeGridOf(region: Pick<LakeRegion, 'centerX' | 'centerZ'>): DrawnGrid {
  const n = LAKE_GRID_N;
  return { originX: region.centerX - LAKE_REGION_SIZE / 2, originZ: region.centerZ - LAKE_REGION_SIZE / 2, spacing: LAKE_REGION_SIZE / (n - 1), cells: n - 1 };
}

/** The drawn height of lake node `idx`: its level when wet, the terrain minus the sink when dry. */
export function lakeNodeY(region: Pick<LakeRegion, 'surface' | 'raw'>, idx: number): number {
  const s = region.surface[idx];
  return Number.isNaN(s) ? region.raw[idx] - LAKE_DRY_SINK_M : s;
}

/**
 * The region sheet the lake module draws (LOCAL coordinates, centred; the mesh stands at (centerX, 0, centerZ)): three's
 * PlaneGeometry over the grid, rotated flat, each vertex at `lakeNodeY`.
 */
export function buildLakeSheetGeometry(region: Pick<LakeRegion, 'surface' | 'raw'>): THREE.PlaneGeometry {
  const n = LAKE_GRID_N;
  const geo = new THREE.PlaneGeometry(LAKE_REGION_SIZE, LAKE_REGION_SIZE, n - 1, n - 1);
  geo.rotateX(-Math.PI / 2);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const wet = new Uint8Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    const col = Math.round((pos.getX(i) / LAKE_REGION_SIZE + 0.5) * (n - 1));
    const row = Math.round((pos.getZ(i) / LAKE_REGION_SIZE + 0.5) * (n - 1));
    const idx = row * n + col;
    pos.setY(i, lakeNodeY(region, idx));
    wet[i] = Number.isNaN(region.surface[idx]) ? 0 : 1;
  }
  // Draw only triangles that touch water (2026-10-05 sweep, perf): an all-dry triangle is sunk LAKE_DRY_SINK_M under the
  // terrain at all three corners — never seen — yet the full 257² sheet was 131k triangles in one draw at the hub for a
  // few lakes. A shore triangle (any wet corner) keeps its tilt, so every drawn triangle and `drawnLakeHeight` agree.
  const src = geo.index!.array;
  const kept = new Uint32Array(src.length);
  let k = 0;
  for (let t = 0; t < src.length; t += 3) {
    const a = src[t], b = src[t + 1], c = src[t + 2];
    if (wet[a] | wet[b] | wet[c]) { kept[k++] = a; kept[k++] = b; kept[k++] = c; }
  }
  geo.setIndex(new THREE.BufferAttribute(kept.slice(0, k), 1));
  return geo;
}

/**
 * The DRAWN lake sheet over ABSOLUTE (x, z): the triangle of `buildLakeSheetGeometry` under the point, interpolated as the
 * rasteriser does (the lake displaces no waves — its whole set is under the 6 m grid's vertex cut-off, pinned by
 * verify:water-body). Null outside the region.
 */
export function drawnLakeHeight(region: LakeRegion, x: number, z: number): number | null {
  const g = lakeGridOf(region);
  if (x < g.originX || z < g.originZ || x > g.originX + LAKE_REGION_SIZE || z > g.originZ + LAKE_REGION_SIZE) return null;
  const n = LAKE_GRID_N, sp = g.spacing;
  return solveDrawnTriangle(x, z, g, (i, j, out) => {
    out[0] = g.originX + i * sp;
    out[1] = lakeNodeY(region, j * n + i);
    out[2] = g.originZ + j * sp;
  });
}
